import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { ArrowLeft, Database, Loader2 } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { TextInput } from '@/components/text-input'
import { type CreateTenantInput, createAdminTenant, PlatformApiError } from '@/lib/api'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/platform/tenants/new')({
  component: NewTenantPage,
})

const TIERS: Array<{ value: CreateTenantInput['tier']; label: string; description: string }> = [
  {
    value: 'personal',
    label: 'Personal',
    description: 'Solo users, strict limits, no SSO.',
  },
  {
    value: 'business',
    label: 'Business',
    description: 'Teams on their own domain. Most tenants.',
  },
  {
    value: 'enterprise',
    label: 'Enterprise',
    description: 'Unlimited users, compliance features, dedicated support.',
  },
]

function NewTenantPage() {
  const navigate = useNavigate()

  const [name, setName] = useState('')
  const [emailDomain, setEmailDomain] = useState('')
  const [tier, setTier] = useState<CreateTenantInput['tier']>('business')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await createAdminTenant({ name: name.trim(), emailDomain: emailDomain.trim(), tier })
      // Navigate back to the list. The new tenant will show up on
      // the next fetch. We don't pass state because the list page
      // does its own fetch on mount — the server is the source of
      // truth, not any optimistic cache.
      void navigate({ to: '/platform/tenants' })
    } catch (err) {
      if (err instanceof PlatformApiError) {
        setError(err.message)
      } else {
        setError('Failed to create tenant. Please try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const disabled = submitting || name.trim().length === 0 || emailDomain.trim().length === 0

  return (
    <div>
      <Link
        to="/platform/tenants"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to tenants
      </Link>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="mt-4 max-w-2xl"
      >
        <header className="mb-6">
          <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/15">
            <Database className="h-5 w-5" />
          </div>
          <h2 className="mt-4 text-xl font-semibold tracking-tight sm:text-2xl">New tenant</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Provisions a fresh Postgres database, runs migrations, and registers the tenant. You'll
            be assigned as the owner automatically.
          </p>
        </header>

        <form
          onSubmit={handleSubmit}
          className="rounded-2xl border border-border bg-card p-6 shadow-sm space-y-5"
          noValidate
        >
          <TextInput
            label="Tenant name"
            type="text"
            required
            autoFocus
            placeholder="Beyond"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            hint="Human-readable label. Shown in the admin UI and on billing."
          />

          <TextInput
            label="Email domain"
            type="text"
            required
            placeholder="beyond.com"
            value={emailDomain}
            onChange={(e) => setEmailDomain(e.currentTarget.value.toLowerCase())}
            hint="Users with this email domain will route to this tenant on sign-in. Business tier auto-verifies the domain; admin is trusted."
          />

          <div className="space-y-2">
            <div className="block text-sm font-medium">Tier</div>
            <div className="grid gap-2">
              {TIERS.map((t) => (
                <label
                  key={t.value}
                  className={cn(
                    'relative flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors',
                    tier === t.value
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/40',
                  )}
                >
                  <input
                    type="radio"
                    name="tier"
                    value={t.value}
                    checked={tier === t.value}
                    onChange={() => setTier(t.value)}
                    className="mt-0.5 h-4 w-4 accent-primary"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium">{t.label}</div>
                    <div className="mt-0.5 text-xs text-muted-foreground">{t.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {error ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          ) : null}

          <div className="flex items-center justify-end gap-3 border-t border-border pt-5">
            <Link
              to="/platform/tenants"
              className="inline-flex items-center rounded-full border border-border px-5 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={disabled}
              className={cn(
                'inline-flex items-center gap-2 rounded-lg px-5 py-2 text-sm font-medium',
                'bg-primary text-primary-foreground shadow-md shadow-primary/20',
                'hover:opacity-90 transition-opacity',
                'disabled:opacity-60 disabled:cursor-not-allowed',
              )}
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Provisioning…
                </>
              ) : (
                'Create tenant'
              )}
            </button>
          </div>
        </form>

        <p className="mt-4 text-xs text-muted-foreground">
          Provisioning takes ~5–10 seconds — we create a Postgres database, run every migration, and
          encrypt the connection string. Don't close this tab during the request.
        </p>
      </motion.div>
    </div>
  )
}
